import { computed, ref, watch } from 'vue'
import { api } from '@/global/services/api'
import { dateService, FORMATS } from '@/global/services/date'
import { useAppContext } from '@/global/services/compositionApi/useAppContext'

const TRANSLATION_NAMESPACE = 'road-maintenance/temporary_regulation/crew_scheduling'

// Maps a row's backend `type` to how it should be displayed. Add/adjust an
// entry here whenever the backend introduces a new row type or the styling
// for an existing one changes — nothing else in this file needs to change.
const ROW_TYPE_STYLE = {
  shift_start: { colorType: 'shift', numbered: false, reorderable: false },
  shift_end: { colorType: 'shift', numbered: false, reorderable: false },
  crew_base: { colorType: 'endpoint', numbered: true, reorderable: false },
  transit: { colorType: 'transit', numbered: false, reorderable: false },
  plan_location: { colorType: 'regulation-item', numbered: true, reorderable: true },
  // Not part of the documented contract yet — returned instead of `plan_location`
  // once a route has already been saved for the day (`source: 'exec_route'`).
  // Styled the same until the backend/product side defines its own behaviour.
  exec_location: { colorType: 'regulation-item', numbered: true, reorderable: false },
  // Not implemented on the backend yet, kept for when it is.
  break: { colorType: 'break', numbered: false, reorderable: false }
}
const DEFAULT_ROW_STYLE = { colorType: 'transit', numbered: false, reorderable: false }

// `time` columns (crew working hours) come back as bare 'HH:mm:ss' strings.
function toDisplayTime (value) {
  return value ? String(value).slice(0, 5) : ''
}

// `planned_start`/`planned_end` are Carbon datetimes (ISO 8601 on the wire).
function toDisplayDateTime (value) {
  if (!value) {
    return ''
  }
  if (dateService.isValid(value)) {
    return dateService.format(value, FORMATS.datetimeShort)
  }
  return value
}

// `row.planned_start`/`row.planned_end` are bare 'HH:mm' strings — the
// backend strips them down to time-only before returning a row (see
// CrewSchedulingService::formatRowTimes) — so they need the schedule's own
// date glued back on to become the full 'Y-m-d H:i:s' datetime the store
// endpoint validates against.
function toApiDateTime (time, date) {
  if (!time || !date) {
    return null
  }
  const fromFormat = 'YYYY-MM-DD HH:mm'
  const combined = `${date} ${time}`
  if (dateService.isValid(combined, fromFormat, true)) {
    return dateService.formatCustom(combined, fromFormat, FORMATS.isoDatetime)
  }
  return null
}

function hasOverlap (raw) {
  return !!raw.flagged
}

// A row (transit or plan/exec location) can carry a nested `break` object when
// the crew's break falls during that leg/item. It's rendered as an extra
// sub-row directly beneath its parent. `planned_start` already comes pre-formatted
// as 'H:i' by the backend, unlike the row-level datetimes.
function mapPause (breakData, translate) {
  if (!breakData) {
    return null
  }

  return {
    name: breakData.name || translate(`${TRANSLATION_NAMESPACE}.pause`),
    arrivalTime: breakData.planned_start || '',
    durationLabel: breakData.duration_label || ''
  }
}

function mapRow (raw, index, translate) {
  raw = raw || {}
  const style = ROW_TYPE_STYLE[raw.type] || DEFAULT_ROW_STYLE
  const colorType = hasOverlap(raw) ? 'overlap' : style.colorType

  const arrivalTime = raw.planned_start
    ? toDisplayDateTime(raw.planned_start)
    : (raw.type === 'shift_start' ? toDisplayTime(raw.time) : '')

  const departureTime = raw.planned_end
    ? toDisplayDateTime(raw.planned_end)
    : (raw.type === 'shift_end' ? toDisplayTime(raw.time) : '')

  return {
    ...raw,
    uid: `${raw.type || 'row'}-${index}`,
    colorType,
    numbered: style.numbered,
    reorderable: style.reorderable,
    name: raw.name || '',
    activities: raw.activities || '',
    address: raw.address || '',
    estimatedKm: raw.distance || '',
    estimatedMin: raw.duration || '',
    durationLabel: raw.duration_label || '',
    arrivalTime,
    departureTime,
    pause: mapPause(raw.break, translate),
    flagReason: raw.flag_reason || null
  }
}

export function useCrewScheduling (options) {
  const { translate } = options || {}
  const { route } = useAppContext()

  // Lets other screens (e.g. a work order's location) deep-link here with a
  // pre-selected crew/date, so the user can jump back and forth between the
  // two screens instead of re-picking both by hand.
  const queryCrewId = route?.query?.crew_id ? Number(route.query.crew_id) : null
  const queryDate = route?.query?.date && dateService.isValid(String(route.query.date), FORMATS.isoDate, true)
    ? String(route.query.date)
    : null

  const search = ref('')
  const edit = ref(false)
  const isLoading = ref(false)
  const isSaving = ref(false)

  const crewOptions = ref([])
  const selectedCrewId = ref(queryCrewId)
  const selectedDate = ref(queryDate || dateService.format(new Date(), FORMATS.isoDate))

  const rows = ref([])
  const scheduleResponse = ref(null)

  const dateFormat = FORMATS.isoDate
  const dateDisplayFormat = 'DD/MM/YYYY'

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: translate('road-maintenance.temporary_regulation')
      },
      {
        text: translate('road-maintenance/temporary_regulation.crew_scheduling'),
        bolded: true
      }
    ]
  }))

  const canSave = computed(() => !!scheduleResponse.value?.meta?.permissions?.can_save)
  const canDelete = computed(() => !!scheduleResponse.value?.meta?.permissions?.can_delete)

  // Rows the backend flagged (overlap, outside working hours, etc. - see
  // CrewSchedulingService::annotateFlags) - each already carries its own
  // human-readable `flagReason` (mapRow's camelCase alias for `flag_reason`).
  const flaggedRows = computed(() => rows.value.filter((row) => row.flagged))
  const hasOverlap = computed(() => flaggedRows.value.length > 0)

  const utilization = computed(() => {
    const summary = scheduleResponse.value?.summary || {}
    return {
      label: translate(`${TRANSLATION_NAMESPACE}.day_utilization`),
      workedLabel: summary.in_work_duration_label || '-',
      shiftLabel: summary.work_duration_label || '-',
      // Not implemented on the backend yet — shown as a placeholder until it is.
      remainingLabel: summary.remaining_duration_label || '-',
      percent: summary.percent ?? 0
    }
  })

  const fetchCrews = async () => {
    try {
      const { data } = await api('road-maintenance').get('crews', {
        pagination: JSON.stringify({ currentPage: 1, perPage: 200 })
      })
      crewOptions.value = (data || []).map((crew) => ({ id: crew.id, name: crew.name }))
      if (!selectedCrewId.value && crewOptions.value.length === 1) {
        selectedCrewId.value = crewOptions.value[0].id
      }
    }
    catch (exception) {
      console.error(exception)
    }
  }

  const fetchSchedule = async (planLocationOrder) => {
    if (!selectedCrewId.value || !selectedDate.value) {
      scheduleResponse.value = null
      rows.value = []
      return
    }

    try {
      isLoading.value = true
      const { data, meta } = await api('road-maintenance').get('work-orders/crew-scheduling', {
        crew_id: selectedCrewId.value,
        date: selectedDate.value,
        ...(planLocationOrder ? { plan_location_order: planLocationOrder } : {})
      })
      scheduleResponse.value = { ...(data || {}), meta }
      rows.value = (data?.rows || []).map((raw, index) => mapRow(raw, index, translate))
    }
    catch (exception) {
      console.error(exception)
      scheduleResponse.value = null
      rows.value = []
    }
    finally {
      isLoading.value = false
    }
  }

  // `immediate` so a deep link that pre-fills both crew and date (e.g. from a
  // work order's location) fetches right away — otherwise the watcher only
  // fires on a later change, and query-provided values never trigger it since
  // they're already set before this runs.
  // Wrapped in an arrow function rather than passed directly — watching
  // multiple sources hands the callback `[newCrewId, newDate]` as its first
  // argument, which would otherwise land in fetchSchedule's `planLocationOrder`
  // param and get sent to the backend as `plan_location_order`.
  watch([selectedCrewId, selectedDate], () => fetchSchedule(), { immediate: true })

  fetchCrews()

  const enterEditMode = () => {
    edit.value = true
  }

  const cancelEditMode = () => {
    edit.value = false
    fetchSchedule()
  }

  // Best-effort save: persists the current (possibly reordered) plan-location
  // sequence via `POST work-orders/crew-scheduling`. Travel distance/duration
  // per item still reflect the originally fetched route — reordering doesn't
  // recompute transit legs client-side, since the "Optimize" step (the one
  // that would ask the backend to recalculate the route) isn't part of the
  // edit flow anymore. Revisit once the backend contract settles.
  const saveChanges = async () => {
    if (!selectedCrewId.value || !selectedDate.value) {
      return
    }

    const items = rows.value
      .filter((row) => row.type === 'plan_location')
      .map((row, index) => ({
        plan_location_id: row.plan_location_id,
        planned_start: toApiDateTime(row.planned_start, selectedDate.value),
        planned_end: toApiDateTime(row.planned_end, selectedDate.value),
        travel_distance: row.distance ?? null,
        travel_duration: row.duration ?? null,
        sort: index + 1
      }))

    const baseGeolocationId = scheduleResponse.value?.crew?.base_geolocation?.id ?? null

    try {
      isSaving.value = true
      await api('road-maintenance').post('work-orders/crew-scheduling', {
        crew_id: selectedCrewId.value,
        date: selectedDate.value,
        start_geolocation_id: baseGeolocationId,
        end_geolocation_id: baseGeolocationId,
        items
      })
      edit.value = false
      await fetchSchedule()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      isSaving.value = false
    }
  }

  // Cancels (deletes) the exec route already saved for this crew/date.
  const cancelSchedule = async () => {
    const execRouteId = scheduleResponse.value?.exec_route_id
    if (!execRouteId) {
      return
    }

    try {
      isSaving.value = true
      await api('road-maintenance').delete(`work-orders/exec-routes/${execRouteId}`)
      await fetchSchedule()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      isSaving.value = false
    }
  }

  const refresh = () => {
    fetchSchedule()
  }

  const shiftDate = (amount) => {
    const current = selectedDate.value ? new Date(selectedDate.value) : new Date()
    current.setDate(current.getDate() + amount)
    selectedDate.value = dateService.format(current, FORMATS.isoDate)
  }

  const goToPreviousDay = () => shiftDate(-1)
  const goToNextDay = () => shiftDate(1)

  const moveRow = ({ index, direction }) => {
    const reorderableIndexes = rows.value
      .map((row, rowIndex) => (row.reorderable ? rowIndex : -1))
      .filter((rowIndex) => rowIndex !== -1)

    const positionInGroup = reorderableIndexes.indexOf(index)
    if (positionInGroup === -1) {
      return
    }

    const swapWithPosition = direction === 'up' ? positionInGroup - 1 : positionInGroup + 1
    if (swapWithPosition < 0 || swapWithPosition >= reorderableIndexes.length) {
      return
    }

    const swapWithIndex = reorderableIndexes[swapWithPosition]
    const newRows = [...rows.value]
    const temp = newRows[index]
    newRows[index] = newRows[swapWithIndex]
    newRows[swapWithIndex] = temp

    // The backend recalculates travel legs and cascades arrival/departure
    // times for the whole day from the new order — blank those columns so
    // stale values from the old order don't linger while that call is in flight.
    rows.value = newRows.map((row) => ({
      ...row,
      estimatedKm: '',
      estimatedMin: '',
      arrivalTime: '',
      departureTime: '',
      durationLabel: '',
      pause: row.pause ? { ...row.pause, arrivalTime: '', durationLabel: '' } : row.pause
    }))

    const planLocationOrder = reorderableIndexes.map((rowIndex) => newRows[rowIndex].plan_location_id)
    fetchSchedule(planLocationOrder)
  }

  return {
    search,
    edit,
    isLoading,
    isSaving,
    breadcrumbs,
    crewOptions,
    selectedCrewId,
    selectedDate,
    dateFormat,
    dateDisplayFormat,
    rows,
    utilization,
    canSave,
    canDelete,
    hasOverlap,
    flaggedRows,
    enterEditMode,
    cancelEditMode,
    saveChanges,
    cancelSchedule,
    refresh,
    goToPreviousDay,
    goToNextDay,
    moveRow
  }
}
